import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BarChart3,
    DollarSign,
    TrendingDown,
    TrendingUp,
    Wallet,
    Download,
    FileSpreadsheet,
    FileText,
    ChevronDown,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useWallet } from '@/hooks/useWallet';
import { useTradingAccounts } from '@/api/trading-accounts/use-trading-accounts';
import { useTransactions } from '@/api/transactions/use-transactions.tsx';
import {
    useCreateTradingAccountDeposit,
    useCreateTradingAccountWithdrawal,
    usePaymentMethods,
} from '@/api/transactions/use-trading-account-transactions';
import { API_ENDPOINTS } from '@/api/endpoints';
import httpClient from '@/api/httpClient';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import TradingAccountDepositForm from './components/TradingAccountDepositForm';
import TradingAccountWithdrawalForm from './components/TradingAccountWithdrawalForm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatAmount, formatCurrencyAmount } from '@/utils/formatters';
import { useAuth } from '@/contexts/AuthContext';

const AccountsPage: React.FC = () => {
    const { t } = useTranslation('accounts');
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();

    const { balances, isLoadingWallets } = useWallet();
    const { data: tradingAccounts, isLoading: isLoadingAccounts } = useTradingAccounts();
    const { data: transactions, isLoading: isLoadingTransactions } = useTransactions();

    const [depositOpen, setDepositOpen] = useState(false);
    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [selectedTradingAccount, setSelectedTradingAccount] = useState<any>(null);

    // Trading account deposit/withdrawal hooks
    // Note: Payment methods will be filtered by currency in the form components
    const { data: paymentMethods = [] } = usePaymentMethods();
    const createDeposit = useCreateTradingAccountDeposit();
    const createWithdrawal = useCreateTradingAccountWithdrawal();

    React.useEffect(() => {
        if (createDeposit.isSuccess) {
            const hasProcessorRedirect =
                !!createDeposit.data?.payment_url ||
                !!createDeposit.data?.widget_url ||
                !!createDeposit.data?.form_data;

            // Keep dialog open for processor flows so embedded checkout can render.
            if (!hasProcessorRedirect) {
                enqueueSnackbar(t('depositSuccess'), { variant: 'success' });
                setDepositOpen(false);
                setSelectedTradingAccount(null);
            }
        }
        if (createDeposit.isError) {
            enqueueSnackbar(t('depositFailed'), { variant: 'error' });
        }
    }, [createDeposit.isSuccess, createDeposit.isError, enqueueSnackbar, t]);

    React.useEffect(() => {
        if (createWithdrawal.isSuccess) {
            enqueueSnackbar(t('withdrawalSuccess'), { variant: 'success' });
            setWithdrawOpen(false);
            setSelectedTradingAccount(null);
        }
        if (createWithdrawal.isError) {
            const errorMessage =
                (createWithdrawal.error as any)?.response?.data?.message ||
                (createWithdrawal.error as any)?.response?.data?.error ||
                (createWithdrawal.error as any)?.message ||
                t('withdrawalFailed');
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    }, [
        createWithdrawal.isSuccess,
        createWithdrawal.isError,
        createWithdrawal.error,
        enqueueSnackbar,
    ]);

    // Auto-refresh data every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            queryClient.invalidateQueries({ queryKey: ['wallets'] });
            queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS],
            });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.TRANSACTIONS.LIST] });
        }, 30000);

        return () => clearInterval(interval);
    }, [queryClient]);

    const handleTradingAccountDepositClick = (account: any) => {
        setSelectedTradingAccount(account);
        setDepositOpen(true);
    };

    const handleTradingAccountWithdrawClick = (account: any) => {
        setSelectedTradingAccount(account);
        setWithdrawOpen(true);
    };

    const downloadStatement = async (format: 'excel' | 'pdf') => {
        try {
            // Fetch order history for the statement using the proper API endpoint
            const orderHistoryResponse = await httpClient.get(API_ENDPOINTS.TRADER.ORDERS.HISTORY);
            const orderHistory = orderHistoryResponse.data;

            // Get trader info from auth context
            const traderInfo = user || {};

            // Calculate account summary
            const accountSummary = {
                totalAccounts: tradingAccounts?.accounts?.length || 0,
                totalBalance:
                    tradingAccounts?.accounts?.reduce(
                        (sum, acc) => sum + Number(acc.balance || 0),
                        0
                    ) || 0,
                totalEquity:
                    tradingAccounts?.accounts?.reduce(
                        (sum, acc) => sum + Number(acc.equity || 0),
                        0
                    ) || 0,
                totalUsedMargin:
                    tradingAccounts?.accounts?.reduce(
                        (sum, acc) => sum + Number(acc.used_margin || 0),
                        0
                    ) || 0,
                totalFreeMargin:
                    tradingAccounts?.accounts?.reduce(
                        (sum, acc) => sum + Number(acc.free_margin || 0),
                        0
                    ) || 0,
            };

            // Calculate order summary
            // API returns { success: true, trades: [...], pagination: null }
            const orders = orderHistory?.trades || [];
            const totalPnL = orders.reduce(
                (sum, order) => sum + Number(order.realized_pnl || 0),
                0
            );
            const totalBalance = accountSummary.totalBalance;

            // Combine orders and transactions into a single chronological list
            const combinedItems: Array<{
                date: Date;
                type: 'transaction' | 'order';
                data: any;
            }> = [
                ...(transactions?.data || []).map((tx: any) => ({
                    date: new Date(tx.created_at),
                    type: 'transaction' as const,
                    data: tx,
                })),
                ...orders.map((order: any) => ({
                    date: new Date(order.created_at),
                    type: 'order' as const,
                    data: order,
                })),
            ].sort((a, b) => a.date.getTime() - b.date.getTime());

            const formatDate = (date: Date) => {
                return date.toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                });
            };

            // Prepare statement data
            const statementData = {
                header: {
                    title: t('tradingAccountStatement'),
                    generatedOn: new Date().toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                    }),
                },
                traderInfo: {
                    name: traderInfo.name || 'N/A',
                    email: traderInfo.email || 'N/A',
                    customerNumber: traderInfo.customer_number || 'N/A',
                    country: traderInfo.country || 'N/A',
                },
                accountSummary,
                accounts: tradingAccounts?.accounts || [],
                combinedItems,
                summary: {
                    totalPnL,
                    totalBalance,
                },
            };

            // Export based on format
            const dateStr = new Date().toISOString().split('T')[0];
            const filename = `trading_statement_${dateStr}`;

            if (format === 'excel') {
                // Excel Export
                const wb = XLSX.utils.book_new();

                // Header sheet
                const headerData = [
                    [statementData.header.title, statementData.header.generatedOn],
                    [],
                    ['TRADER INFORMATION'],
                    ['Name', statementData.traderInfo.name],
                    ['Email', statementData.traderInfo.email],
                    ['Customer Number', statementData.traderInfo.customerNumber],
                    ['Country', statementData.traderInfo.country],
                ];
                const wsHeader = XLSX.utils.aoa_to_sheet(headerData);
                XLSX.utils.book_append_sheet(wb, wsHeader, 'Info');

                // Statement sheet
                const statementRows = [
                    ['Date', 'Type', 'Description', 'Amount', 'Status', 'Details'],
                    ...combinedItems.map(item => {
                        if (item.type === 'transaction') {
                            const tx = item.data;
                            return [
                                formatDate(item.date),
                                'Transaction',
                                `${tx.type} - ${tx.asset?.symbol || tx.asset_id || 'Unknown'}`,
                                formatAmount(Number(tx.amount || 0), 5),
                                tx.status,
                                tx.notes || '',
                            ];
                        } else {
                            const order = item.data;
                            return [
                                formatDate(item.date),
                                'Order',
                                `${order.type} ${order.side} ${order.instrument?.symbol || 'N/A'}`,
                                formatAmount(Number(order.lot_size || 0), 5),
                                order.status,
                                `Price: ${order.price ? formatAmount(Number(order.price), 5) : 'Market'}, P&L: ${formatAmount(Number(order.realized_pnl || 0), 5)}`,
                            ];
                        }
                    }),
                ];
                const wsStatement = XLSX.utils.aoa_to_sheet(statementRows);
                XLSX.utils.book_append_sheet(wb, wsStatement, 'Statement');

                XLSX.writeFile(wb, `${filename}.xlsx`);
            } else if (format === 'pdf') {
                // PDF Export
                const doc = new jsPDF();
                let yPos = 20;

                // Header
                doc.setFontSize(16);
                doc.text(statementData.header.title, 14, yPos);
                yPos += 10;
                doc.setFontSize(10);
                doc.text(`Generated on: ${statementData.header.generatedOn}`, 14, yPos);
                yPos += 15;

                // Trader Info
                doc.setFontSize(12);
                doc.text('Trader Information', 14, yPos);
                yPos += 8;
                doc.setFontSize(10);
                doc.text(`Name: ${statementData.traderInfo.name}`, 14, yPos);
                yPos += 6;
                doc.text(`Email: ${statementData.traderInfo.email}`, 14, yPos);
                yPos += 6;
                doc.text(`Customer Number: ${statementData.traderInfo.customerNumber}`, 14, yPos);
                yPos += 10;

                // Statement Table
                const tableData = combinedItems.map(item => {
                    if (item.type === 'transaction') {
                        const tx = item.data;
                        return [
                            formatDate(item.date),
                            'Transaction',
                            `${tx.type} - ${tx.asset?.symbol || tx.asset_id || 'Unknown'}`,
                            formatAmount(Number(tx.amount || 0), 5),
                            tx.status,
                        ];
                    } else {
                        const order = item.data;
                        return [
                            formatDate(item.date),
                            'Order',
                            `${order.type} ${order.side} ${order.instrument?.symbol || 'N/A'}`,
                            formatAmount(Number(order.lot_size || 0), 5),
                            order.status,
                        ];
                    }
                });

                autoTable(doc, {
                    startY: yPos,
                    head: [['Date', 'Type', 'Description', 'Amount', 'Status']],
                    body: tableData,
                    theme: 'striped',
                    headStyles: { fillColor: [66, 139, 202] },
                });

                doc.save(`${filename}.pdf`);
            }

            enqueueSnackbar(t('statementDownloadSuccess', { format: format.toUpperCase() }), {
                variant: 'success',
            });
        } catch (error: any) {
            console.error('Statement download error:', error);
            const errorMessage =
                error?.response?.data?.message || error?.message || t('statementDownloadFailed');
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    const formatCurrency = (value: number, currency: string = 'USD') => {
        const formatted = formatAmount(value, 2);
        // Add currency symbol prefix
        const currencySymbols: Record<string, string> = {
            USD: '$',
            EUR: '€',
            GBP: '£',
            JPY: '¥',
            AUD: 'A$',
            CAD: 'C$',
            CHF: 'Fr',
            CNY: '¥',
            ILS: '₪',
        };
        const symbol = currencySymbols[currency] || currency;
        return `${symbol}${formatted}`;
    };

    // Calculate account statistics
    const accountStats = {
        totalAccounts: tradingAccounts?.accounts?.length || 0,
        totalEquity:
            tradingAccounts?.accounts?.reduce((sum, acc) => sum + Number(acc.equity), 0) || 0,
        totalUsedMargin:
            tradingAccounts?.accounts?.reduce((sum, acc) => sum + Number(acc.used_margin), 0) || 0,
        totalFreeMargin:
            tradingAccounts?.accounts?.reduce((sum, acc) => sum + Number(acc.free_margin), 0) || 0,
        totalUnrealizedPnL:
            tradingAccounts?.accounts?.reduce((sum, acc) => sum + Number(acc.unrealized_pnl), 0) ||
            0,
    };

    // Filter recent transactions (last 30 days)
    const recentTransactions =
        transactions?.data?.filter(tx => {
            const txDate = new Date(tx.created_at);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            return txDate >= thirtyDaysAgo;
        }) || [];

    const availableBalance = balances.reduce(
        (sum, balance) => sum + balance.available * (balance.usdValue / balance.balance || 0),
        0
    );

    const lockedBalance = balances.reduce(
        (sum, balance) => sum + balance.locked * (balance.usdValue / balance.balance || 0),
        0
    );

    if (isLoadingWallets || isLoadingAccounts || isLoadingTransactions) {
        return (
            <div className="min-h-screen bg-background">
                <div className="ml-0 md:ml-8 p-1 sm:p-2 pt-5 sm:pt-2 min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Main Content */}
            <div className="ml-0 md:ml-8 p-1 sm:p-2 pt-5 sm:pt-2 min-h-screen">
                <div className="grid grid-cols-1 gap-1 sm:gap-2 h-full">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                        <Card className="bg-card border-border text-center">
                            <CardContent className="p-2 sm:p-3">
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    {t('totalAssets')}
                                </h3>
                                <div className="text-2xl sm:text-3xl font-bold text-green-500 mb-1">
                                    {balances.length}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {t('differentCryptocurrencies')}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="bg-card border-border text-center">
                            <CardContent className="p-2 sm:p-3">
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    {t('availableBalance')}
                                </h3>
                                <div className="text-xl sm:text-2xl font-bold text-primary mb-1">
                                    {formatCurrency(availableBalance, 'USD')}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {t('readyForTrading')}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="bg-card border-border text-center">
                            <CardContent className="p-2 sm:p-3">
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    {t('lockedBalance')}
                                </h3>
                                <div className="text-xl sm:text-2xl font-bold text-yellow-500 mb-1">
                                    {formatCurrency(lockedBalance, 'USD')}
                                </div>
                                <p className="text-sm text-muted-foreground">{t('inOpenOrders')}</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Download Statements */}
                    <Card className="bg-card border-border mb-2">
                        <CardContent className="p-2 sm:p-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-foreground mb-1">
                                        {t('downloadStatements')}
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        {t('downloadStatementDescription')}
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="outline" size="sm" className="gap-2">
                                                <Download className="h-4 w-4" />
                                                {t('downloadStatement')}
                                                <ChevronDown className="h-4 w-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem
                                                onClick={() => downloadStatement('excel')}
                                                className="flex items-center gap-2"
                                            >
                                                <FileSpreadsheet className="h-4 w-4" />
                                                {t('downloadAsExcel')}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                onClick={() => downloadStatement('pdf')}
                                                className="flex items-center gap-2"
                                            >
                                                <FileText className="h-4 w-4" />
                                                {t('downloadAsPDF')}
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Trading Accounts Stats */}
                    {tradingAccounts && tradingAccounts.accounts.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                            <Card className="bg-card border-border text-center">
                                <CardContent className="p-2 sm:p-3">
                                    <div className="flex items-center justify-center mb-2">
                                        <Wallet className="h-5 w-5 text-primary mr-2" />
                                        <h3 className="text-lg font-semibold text-foreground">
                                            {t('tradingAccounts')}
                                        </h3>
                                    </div>
                                    <div className="text-2xl sm:text-3xl font-bold text-primary mb-1">
                                        {accountStats.totalAccounts}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {t('activeAccounts')}
                                    </p>
                                </CardContent>
                            </Card>

                            <Card className="bg-card border-border text-center">
                                <CardContent className="p-2 sm:p-3">
                                    <div className="flex items-center justify-center mb-2">
                                        <DollarSign className="h-5 w-5 text-green-400 mr-2" />
                                        <h3 className="text-lg font-semibold text-foreground">
                                            {t('totalEquity')}
                                        </h3>
                                    </div>
                                    <div className="text-xl sm:text-2xl font-bold text-green-400 mb-1">
                                        {formatCurrency(accountStats.totalEquity, 'USD')}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {t('accountValue')}
                                    </p>
                                </CardContent>
                            </Card>

                            <Card className="bg-card border-border text-center">
                                <CardContent className="p-2 sm:p-3">
                                    <div className="flex items-center justify-center mb-2">
                                        <BarChart3 className="h-5 w-5 text-chart-2 mr-2" />
                                        <h3 className="text-lg font-semibold text-foreground">
                                            {t('usedMargin')}
                                        </h3>
                                    </div>
                                    <div className="text-xl sm:text-2xl font-bold text-chart-2 mb-1">
                                        {formatCurrency(accountStats.totalUsedMargin, 'USD')}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {t('inPositions')}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Trading Accounts Table */}
                    {tradingAccounts && tradingAccounts.accounts.length > 0 && (
                        <Card className="bg-card border-border overflow-hidden">
                            <CardHeader className="p-2 sm:p-3 border-b border-border">
                                <CardTitle className="text-lg font-semibold text-foreground">
                                    {t('tradingAccounts')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="overflow-auto max-h-96 md:max-h-[600px]">
                                    <Table>
                                        <TableHeader className="sticky top-0 bg-muted/50">
                                            <TableRow className="border-border">
                                                <TableHead className="text-muted-foreground font-semibold">
                                                    {t('account')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold text-right">
                                                    {t('balance')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold text-right">
                                                    {t('equity')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold text-right">
                                                    {t('usedMargin')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold text-right">
                                                    {t('freeMargin')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold text-right">
                                                    {t('dailyPnL')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold text-center">
                                                    {t('status')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold text-center">
                                                    {t('actions')}
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {tradingAccounts.accounts.map(account => (
                                                <TableRow
                                                    key={account.id}
                                                    className="border-border hover:bg-muted/50"
                                                >
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <Avatar className="w-8 h-8">
                                                                <AvatarFallback className="bg-primary text-white text-sm">
                                                                    {account.base_currency[0]}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            <div>
                                                                <div className="font-semibold text-foreground text-sm sm:text-base">
                                                                    {account.base_currency}{' '}
                                                                    {t('account')}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {account.leverage}:1{' '}
                                                                    {t('leverage')}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-right text-foreground font-mono text-sm">
                                                        {formatCurrency(
                                                            account.balance,
                                                            account.base_currency
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right text-foreground font-mono text-sm">
                                                        {formatCurrency(
                                                            account.equity,
                                                            account.base_currency
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right text-chart-2 font-mono text-sm">
                                                        {formatCurrency(
                                                            account.used_margin,
                                                            account.base_currency
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right text-green-400 font-mono text-sm">
                                                        {formatCurrency(
                                                            account.free_margin,
                                                            account.base_currency
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        className={`text-right font-mono text-sm ${
                                                            account.daily_pnl >= 0
                                                                ? 'text-green-400'
                                                                : 'text-red-400'
                                                        }`}
                                                    >
                                                        {formatCurrency(
                                                            Math.abs(account.daily_pnl),
                                                            account.base_currency
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <div
                                                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                                account.margin_status.can_trade
                                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                                    : account.margin_status
                                                                            .margin_call
                                                                      ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                                                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                                                            }`}
                                                        >
                                                            {account.margin_status.can_trade
                                                                ? t('active')
                                                                : account.margin_status.margin_call
                                                                  ? t('marginCall')
                                                                  : t('liquidation')}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <div className="flex flex-col md:flex-row gap-1 justify-center">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() =>
                                                                    handleTradingAccountDepositClick(
                                                                        account
                                                                    )
                                                                }
                                                                className="text-xs min-w-0 md:min-w-[70px] text-green-500 border-green-500/30 hover:bg-green-500/10 hover:border-green-500"
                                                            >
                                                                {t('deposit')}
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Recent Transactions Table */}
                    {recentTransactions.length > 0 && (
                        <Card className="bg-card border-border overflow-hidden">
                            <CardHeader className="p-2 sm:p-3 border-b border-border">
                                <CardTitle className="text-lg font-semibold text-foreground">
                                    {t('recentTransactions')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="overflow-auto max-h-96 md:max-h-[600px]">
                                    <Table>
                                        <TableHeader className="sticky top-0 bg-muted/50">
                                            <TableRow className="border-border">
                                                <TableHead className="text-muted-foreground font-semibold">
                                                    {t('type')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold">
                                                    {t('asset')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold text-right">
                                                    {t('amount')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold text-center">
                                                    {t('status')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold">
                                                    {t('notes')}
                                                </TableHead>
                                                <TableHead className="text-muted-foreground font-semibold text-right">
                                                    {t('date')}
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {recentTransactions.slice(0, 10).map(transaction => (
                                                <TableRow
                                                    key={transaction.id}
                                                    className="border-border hover:bg-muted/50"
                                                >
                                                    <TableCell>
                                                        <div
                                                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                                transaction.type === 'deposit'
                                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                                    : transaction.type === 'bonus'
                                                                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                                                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                                                            }`}
                                                        >
                                                            {transaction.type === 'deposit'
                                                                ? t('depositLabel')
                                                                : transaction.type === 'bonus'
                                                                  ? t('bonus')
                                                                  : t('withdrawalLabel')}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <Avatar className="w-6 h-6">
                                                                <AvatarFallback className="bg-primary text-white text-xs">
                                                                    {transaction.asset
                                                                        ?.symbol?.[0] ||
                                                                        transaction.asset_id?.[0] ||
                                                                        'T'}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            <span className="text-foreground text-sm">
                                                                {transaction.asset?.symbol ||
                                                                    transaction.asset_id ||
                                                                    'Unknown'}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-right text-foreground font-mono text-sm">
                                                        {formatCurrencyAmount(
                                                            parseFloat(transaction.amount || 0),
                                                            transaction.currency ||
                                                                transaction.asset?.symbol ||
                                                                transaction.trading_account
                                                                    ?.base_currency ||
                                                                'USD'
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <div
                                                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                                transaction.status === 'COMPLETED'
                                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                                    : transaction.status ===
                                                                        'PENDING'
                                                                      ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                                                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                                                            }`}
                                                        >
                                                            {transaction.status
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                                transaction.status
                                                                    .slice(1)
                                                                    .toLowerCase()}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell
                                                        translate="no"
                                                        className="text-muted-foreground text-sm"
                                                    >
                                                        {transaction.notes || '-'}
                                                    </TableCell>
                                                    <TableCell className="text-right text-muted-foreground text-sm">
                                                        <DateTimeFormatter
                                                            date={transaction.created_at}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Trading Account Deposit Dialog */}
                <Dialog open={depositOpen && selectedTradingAccount} onOpenChange={setDepositOpen}>
                    <DialogContent className="bg-background border-border text-foreground max-w-md max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>
                                {t('depositToAccount', {
                                    currency: selectedTradingAccount?.base_currency,
                                })}
                            </DialogTitle>
                        </DialogHeader>
                        <TradingAccountDepositForm
                            account={selectedTradingAccount}
                            paymentMethods={paymentMethods}
                            onSubmit={async data => {
                                try {
                                    const result = await createDeposit.mutateAsync(data);
                                    return result;
                                } catch (error) {
                                    throw error;
                                }
                            }}
                            isLoading={createDeposit.isPending}
                            onCancel={() => {
                                setDepositOpen(false);
                                setSelectedTradingAccount(null);
                            }}
                        />
                    </DialogContent>
                </Dialog>

                {/* Trading Account Withdrawal Dialog */}
                <Dialog
                    open={withdrawOpen && selectedTradingAccount}
                    onOpenChange={setWithdrawOpen}
                >
                    <DialogContent className="bg-background border-border text-foreground max-w-md max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>
                                {t('withdrawFromAccount', {
                                    currency: selectedTradingAccount?.base_currency,
                                })}
                            </DialogTitle>
                        </DialogHeader>
                        <TradingAccountWithdrawalForm
                            account={selectedTradingAccount}
                            paymentMethods={paymentMethods}
                            onSubmit={async data => {
                                try {
                                    const result = await createWithdrawal.mutateAsync(data);
                                    return result;
                                } catch (error) {
                                    throw error;
                                }
                            }}
                            isLoading={createWithdrawal.isPending}
                            onCancel={() => {
                                setWithdrawOpen(false);
                                setSelectedTradingAccount(null);
                            }}
                        />
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
};

export default AccountsPage;
